import {
  sendImpressionBatch,
  sendImpressionsOnUnload,
  SendResult,
} from "@/api/analytics";

import { impressionConfig } from "./config";

import type { AnalyticsEvent } from "@/schemas";

let queue: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let persistTimer: ReturnType<typeof setTimeout> | undefined;
let retryAttempt = 0;
let failureStreak = 0; // consecutive failed flushes; backs the interval off up to 5 min
let flushing = false;

// ± flushJitterRatio spread so many clients don't retry in lockstep against a struggling server.
const jitter = (ms: number): number =>
  ms * (1 + (Math.random() - 0.5) * 2 * impressionConfig.flushJitterRatio);

// ── Persistence (survives reload / deploy) ──

/** Serialize the queue to localStorage now, cancelling any pending debounced write. */
const persist = (): void => {
  if (persistTimer) {
    clearTimeout(persistTimer);
    persistTimer = undefined;
  }
  try {
    localStorage.setItem(impressionConfig.storageKey, JSON.stringify(queue));
  } catch {
    /* quota / storage disabled — the in-memory queue still works */
  }
};

/**
 * Debounced persist for the enqueue hot path. Impressions fire steadily during active browsing;
 * coalescing them into one write per idle gap keeps the JSON.stringify (up to queueMaxEvents
 * objects) + synchronous setItem off the main thread on every event. Crash recovery loses at
 * most the debounce window — unload always persists synchronously via flushOnUnload.
 */
const persistSoon = (): void => {
  if (persistTimer) return;
  persistTimer = setTimeout(persist, impressionConfig.persistDebounceMs);
};

const hydrate = (): void => {
  try {
    const raw = localStorage.getItem(impressionConfig.storageKey);
    if (!raw) return;
    const parsed = JSON.parse(raw) as AnalyticsEvent[];
    const cutoff = Date.now() - impressionConfig.eventTtlMs;
    queue = parsed
      .filter((e) => Date.parse(e.event_time) >= cutoff) // drop stale
      .slice(-impressionConfig.queueMaxEvents); // enforce cap
  } catch {
    queue = [];
  }
};

// ── Flush ──

const scheduleFlush = (): void => {
  if (flushTimer) return;
  // Under repeated failure the base interval doubles per streak, capped at flushBackoffMaxMs (5 min),
  // then jittered — so a struggling server isn't hammered every 30s in lockstep.
  const base = Math.min(
    impressionConfig.flushIntervalMs * 2 ** failureStreak,
    impressionConfig.flushBackoffMaxMs,
  );
  flushTimer = setTimeout(() => void flush(), jitter(base));
};

const scheduleRetry = (): void => {
  if (retryAttempt >= impressionConfig.retryMaxAttempts) {
    // Short retry burst exhausted; fall back to the (now backed-off) periodic flush so a struggling
    // server keeps getting slower, jittered attempts instead of being abandoned until the next enqueue.
    retryAttempt = 0;
    scheduleFlush();
    return;
  }
  const delay = impressionConfig.retryBaseMs * 2 ** retryAttempt; // 4s, 8s, 16s
  retryAttempt += 1;
  retryTimer = setTimeout(() => void flush(), jitter(delay));
};

const flush = async (): Promise<void> => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = undefined;
  }
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = undefined;
  }
  if (flushing || queue.length === 0) return;

  flushing = true;
  const batch = queue.slice(0, impressionConfig.maxEventsPerRequest);

  let result: SendResult;
  try {
    result = await sendImpressionBatch(batch);
  } catch {
    result = "retry";
  } finally {
    flushing = false;
  }

  if (result === "retry") {
    failureStreak = Math.min(failureStreak + 1, 20); // grow the backoff (capped in scheduleFlush)
    scheduleRetry(); // keep the batch queued, back off
    return;
  }

  // "ok" or "drop": remove the batch either way ("drop" is a permanent reject, never retried).
  const sentIds = new Set(batch.map((e) => e.event_id));
  const remaining = queue.filter((e) => !sentIds.has(e.event_id));
  queue.splice(0, queue.length, ...remaining);

  retryAttempt = 0;
  failureStreak = 0; // a completed send resets the backoff to the base interval
  persist();
  if (queue.length > 0) scheduleFlush(); // more waiting -> keep draining
};

/**
 * Send on unload (fetch keepalive), best-effort. Optimistically clears the sent batch — the
 * server dedups on event_id, so a crash-then-rehydrate resend is safe; any tail beyond one
 * request stays persisted for the next load.
 */
const flushOnUnload = (): void => {
  const batch = queue.slice(0, impressionConfig.maxEventsPerRequest);
  if (batch.length === 0) return;
  sendImpressionsOnUnload(batch);

  const sentIds = new Set(batch.map((e) => e.event_id));
  const remaining = queue.filter((e) => !sentIds.has(e.event_id));
  queue.splice(0, queue.length, ...remaining);

  persist();
};

const enqueue = (event: AnalyticsEvent): void => {
  queue.push(event);
  if (queue.length > impressionConfig.queueMaxEvents) {
    queue.splice(0, queue.length - impressionConfig.queueMaxEvents); // drop oldest
  }
  persistSoon();
  if (queue.length >= impressionConfig.flushMaxEvents) {
    flush();
  } else {
    scheduleFlush();
  }
};

const getQueue = (): readonly AnalyticsEvent[] => queue;

// Recover anything left by a previous load and drain it on the normal cadence.
hydrate();
if (queue.length > 0) {
  scheduleFlush();
}

export { enqueue, flush, flushOnUnload, getQueue };
